import { BaseInput, Button, ButtonVariant, DateInput } from "shared/components";
import { CombinedIncomeLayout } from "../components/Layout";
import { COMBINED_INCOME_FORM_ID } from "../constants";
import { toast } from "react-toastify";
import { CombinedIncomeSubStep } from "../../../constants";
import { useFormTyped } from "shared/hooks/use-form-typed";
import { object, string } from "yup";
import { DateTime } from "luxon";
import { useCombinedIncome } from "../context";
import { getErrorMessage } from "utils/error";
import { IncomeType } from "shared/types";

type OtherIncomeDetailsFormData = {
  description: string;
  paid_from: string;
  start_date: string;
};

const validationSchema = object({
  description: string().required("Income description is required"),
  paid_from: string().required("Paid by / from is required"),
  start_date: string()
    .required("Start date is required")
    .test("date-in-past", "Start date should be in the past", (value) => {
      if (!value) return false;
      const date = DateTime.fromISO(value);
      return date.isValid && date <= DateTime.now();
    })
});

export const OtherIncomeDetailsStep = () => {
  const { updateStepMutation, stepData, refetchVerification } =
    useCombinedIncome();

  const { register, onSubmit } = useFormTyped<OtherIncomeDetailsFormData>(
    {
      description: stepData.description || "",
      paid_from: stepData.paid_from || "",
      start_date: stepData.start_date || ""
    },
    validationSchema
  );

  const handleSubmit = (formValues: OtherIncomeDetailsFormData) => {
    updateStepMutation.mutate(
      {
        lastStep: CombinedIncomeSubStep.PreTaxIncome,
        description: formValues.description,
        paid_from: formValues.paid_from,
        start_date: formValues.start_date
      },
      {
        onSuccess: () => {
          refetchVerification();
        },
        onError: (error) => {
          toast.error(
            getErrorMessage(error) || "Failed to save income details"
          );
        }
      }
    );
  };

  return (
    <form
      id={COMBINED_INCOME_FORM_ID}
      onSubmit={onSubmit({ onSuccess: handleSubmit })}
    >
      <CombinedIncomeLayout
        title="Add income details"
        footer={
          <Button
            className="w-[125px]"
            type="submit"
            form={COMBINED_INCOME_FORM_ID}
            loading={updateStepMutation.isPending}
            variant={ButtonVariant.PRIMARY}
          >
            Continue
          </Button>
        }
      >
        <BaseInput
          {...register("description")}
          id="description"
          label="Income description"
          labelClassName="mt-4"
          required
        />

        <BaseInput
          {...register("paid_from")}
          id="paid-from"
          label="Paid by / from"
          placeholder="Nurse"
          labelClassName="mt-4"
          required
        />

        <div className="mt-4">
          <DateInput
            {...register("start_date")}
            id="start-date"
            label="Start date"
            ignoreDay={stepData.currentIncomeType !== IncomeType.OfferLetter}
            required
          />
        </div>
      </CombinedIncomeLayout>
    </form>
  );
};
